import React, { useState, useRef, useImperativeHandle, forwardRef } from "react";
import PropTypes from "prop-types";
import ConfirmationDialog from "./content/ConfirmationDialog";
import { Accordion, AccordionTab } from "../../../components/Accordion";
// import { InputText, Label } from "../../../components/Inputs";
import { DeleteIconButton, UpIconButton, DownIconButton, CopyIconButton } from "../../../components/Buttons";
import BasicPageContentForm from "./content/BasicPageContentForm";
import {
  ContentMenu, ContentMenuLabelMap,
  getContentTypeFromId, getContentForm,
  getContentDeleteConfirmationText,
  isContentFromClipboard
} from "./Utils";
import { copyToClipboard, getClipboardSize, isClipboardEmpty, pasteFromClipboard } from "../ClipBoard";
import { notifyInfo, notifySuccess } from "../../../components/Utils";
import ClipBoardDialog from "./ClipBoardDialog";

const PageForm = forwardRef(({ className, model, pageId }, ref) => {
  const initialContents = model.contents || [];
  const [contents, setContents] = useState(initialContents);
  const [activeIndex, setActiveIndex] = useState(0);
  const [draggable, setDraggable] = useState(false);

  const isTabOpenListRef = useRef(initialContents.map(() => false));
  const basicDataRef = useRef(null);
  const formRefs = useRef([]);
  const dialogRef = useRef(null);
  const clipboardDialogRef = useRef(null);

  const createRefMaker = (index) => (r) => { formRefs.current[index] = r; };

  const getCurrentTabContent = (index) => {
    const activeIdx = (index !== undefined && index !== null) ? index : activeIndex;
    const formRef = formRefs.current[activeIdx];
    if (activeIdx !== null && formRef) {
      return formRef.getModel(true);
    }
    return null;
  };

  const updateCurrentContent = () => {
    // get current tab content before closing it
    const oldContents = [...contents];
    if (activeIndex !== null) {
      const currentData = getCurrentTabContent();
      if (currentData) {
        oldContents[activeIndex] = JSON.parse(JSON.stringify(currentData));
      }
    }
    // check list of open contents
    isTabOpenListRef.current.forEach((isOpen, index) => {
      if (isOpen) {
        const currentData = getCurrentTabContent(index);
        if (currentData) {
          oldContents[index] = JSON.parse(JSON.stringify(currentData));
        }
      }
    });
    return oldContents;
  };

  const validate = () => {
    let isValid = true;
    // basic data
    const basicModel = basicDataRef.current ? basicDataRef.current.getModel() : null;
    if (!basicModel) {
      return null;
    }
    // content list
    basicModel.contents = [];
    contents.forEach((item, index) => {
      const formRef = formRefs.current[index];
      if (formRef) {
        let data = formRef.getModel();
        if (!data) {
          isValid = false;
          data = item;
        }
        basicModel.contents.push({ ...data, position: (index + 1) });
      }
    });
    if (!isValid) {
      // setContents(basicModel.contents);
      return null;
    }
    return basicModel;
  };

  useImperativeHandle(ref, () => ({
    getModel: () => {
      const basicModel = validate();
      if (!basicModel) {
        return null;
      }
      // set model id if present
      const { id } = model;
      if (id) {
        basicModel.id = id;
        basicModel.contents = basicModel.contents.map(content => ({
          ...content,
          page_id: id
        }));
      }
      return basicModel;
    }
  }));

  const changeTab = (newIndex) => {
    const formRef = formRefs.current[activeIndex];
    // get current tab content before closing it
    if (formRef) {
      const src = formRef.getModel(true);
      const newContents = contents.map((item, index) =>
        index === activeIndex ? { ...item, ...src } : item
      );
      setContents(newContents);
      setActiveIndex(newIndex);
    }
  };

  const onOpenTabsChanged = (index, isOpen) => {
    isTabOpenListRef.current[index] = isOpen;
  };

  const onDraggable = (value) => {
    // get current tab content before closing it
    const oldContents = updateCurrentContent();
    setContents(oldContents);
    setDraggable(value);
  };

  const addContent = (content_type_id, position, type) => {
    // console.log("PageForm.addContent()", content_type_id, position);
    // update old contents
    const oldContents = updateCurrentContent();
    // add new content
    const newItemIndex = position ? (position - 1) : oldContents.length - 1;
    const handleAddition = (items) => {
      oldContents.splice(position - 1, 0, ...items);
      setContents([...oldContents]);
      setActiveIndex(newItemIndex);
    };
    if (isContentFromClipboard(type)) {
      if (isClipboardEmpty()) {
        return notifyInfo({
          summary: "Zwischenablage is leer",
          detail: "Es kann nichts hinzugefügt werden!"
        });
      } else if (getClipboardSize() === 1) {
        return handleAddition([ pasteFromClipboard() ]);
      }
      clipboardDialogRef.current.show(true, handleAddition);
    } else {
      handleAddition([
        {
          content_type_id,
          title: "",
          subtitle: "",
          body: ""
        }
      ]);
    }
  };

  const confirmContentRemoval = (type, currentIndex) => {
    const confirmData = getContentDeleteConfirmationText(type);
    if (confirmData) {
      dialogRef.current.show(
        true,
        confirmData,
        () => removeContent(currentIndex)
      );
    }
  };

  const removeContent = (currentIndex) => {
    // console.log("PageForm.removeContent()", currentIndex);
    const oldContents = updateCurrentContent();
    const newContents = oldContents.filter((_, index) => index !== currentIndex);
    setContents(newContents);
  };

  const move = (currentIndex, up) => {
    const targetIndex = up ? currentIndex - 1 : currentIndex + 1;
    const oldContents = updateCurrentContent();
    // build new content
    const newContents = [];
    const src = formRefs.current[currentIndex].getModel(true);
    let tgt = formRefs.current[targetIndex].getModel(true);
    tgt = { ...oldContents[targetIndex], ...tgt };
    for (let index = 0; index < oldContents.length; index++) {
      if (index === currentIndex) {
        newContents.push(tgt);
      } else if (index === targetIndex) {
        newContents.push(src);
      } else {
        newContents.push({ ...oldContents[index] });
      }
    }
    setContents(newContents);
    setActiveIndex(targetIndex);
  };

  const onItemDragged = (srcIndex, tgtIndex) => {
    // update old contents if necessary
    const newContents = [...contents];
    if (activeIndex) {
      // get current tab content
      const currentData = getCurrentTabContent();
      newContents[activeIndex] = { ...newContents[activeIndex], ...currentData };
    }
    // move item from srcIndex to tgtIndex
    const items = newContents.splice(srcIndex, 1);
    newContents.splice(tgtIndex, 0, ...items);
    setContents(newContents);
  };

  const copy = (index) => {
    const data = formRefs.current[index].getModel(true);
    const num = copyToClipboard(JSON.parse(JSON.stringify(data)));
    notifySuccess({
      summary: "Zwischenablage",
      detail: `Der Inhalt wurde in die Zwischenablage hinzugefügt. Sie enthält nun ${num} Element${num > 0 ? "e" : ""}`
    });
  };

  const prefix = Math.random();
  const isTabOpenList = isTabOpenListRef.current;
  // console.log("PageForm.render() - activeIndex", activeIndex, "contents", contents);
  const bodyMarkup = contents
    .filter(item => item !== null)
    .map((item, index) => {
      const { content_type_id, ...itemModel } = item;
      const type = getContentTypeFromId(content_type_id);
      const Component = getContentForm(type);
      if (!Component) {
        console.error("<< Content Type Error: unknown content type", type, item);
        return null;
      }
      const menu = (
        <div className="display-inline-block- clearfix">
          <UpIconButton
            className="w3-border margin-rht pad-sm"
            onClick={() => move(index, true)}
            disabled={!(contents.length > 1 && index > 0)}
          />
          <DownIconButton
            className="w3-border margin-rht pad-sm"
            onClick={() => move(index, false)}
            disabled={!(contents.length > 1 && index < contents.length - 1)}
          />
          <CopyIconButton
            className="w3-border margin-rht pad-sm"
            onClick={() => copy(index)}
            title="in die Zwischenablage kopieren"
          />
          <DeleteIconButton
            className="w3-border pad-sm"
            onClick={() => confirmContentRemoval(type, index)}
          />
        </div>
      );
      const counter = `${index < 9 ? "0" : ""}${index + 1}. `;
      // const headerSuffix = itemModel.title ? `${itemModel.title}` : ContentMenuLabelMap[type];
      return (
        // <AccordionTab
        //   key={`${prefix}-${index}`}
        //   header={counter  + headerSuffix}
        //   menu={draggable ? null : menu}
        //   className={`${draggable ? "" : "margin-top margin-lr"}`}
        //   stateOpen={isTabOpenList[index]}
        // >
        //   <Component
        //     ref={createRefMaker(index)}
        //     className="w3-border-top-"
        //     model={itemModel}
        //     pageId={pageId}
        //   />
        // </AccordionTab>
        <AccordionTabContent
          key={`${prefix}-${index}`}
          counter={counter}
          defaultTitle={ContentMenuLabelMap[type]}
          menu={draggable ? null : menu}
          className={`${draggable ? "" : "margin-top margin-lr"}`}
          stateOpen={isTabOpenList[index]}
          _ref={createRefMaker(index)}
          model={itemModel}
          pageId={pageId}
          Component={Component}
        />
      );
    });
  const mainKey = draggable ? Math.random() : "pageForm";
  return (
    <div className={``}>
      <BasicPageContentForm ref={basicDataRef} className={className} model={model}/>
      <div className="w3-border w3-light-grey- bg-secondary margin-big-top pad">
        <ContentMenu
          className="w3-right"
          onAddContent={addContent}
          positions={contents.length + 1}
          draggable={draggable}
          onChangeDraggable={value => onDraggable(value)}
          // onChangeDraggable={value => setDraggable(value)}
        />
        <h4 className="no-margin pad-sm-tb">{"Inhalte"}</h4>
      </div>
      <div className="clearfix margin-big-top pad-btm w3-border ">
        <Accordion
          id="pageContents"
          key={mainKey}
          className="clearfix"
          activeIndex={draggable ? null : activeIndex}
          onTabChange={(event) => changeTab(event.index)}
          onItemDragged={onItemDragged}
          notDraggable={!draggable}
          singleActiveIndex={false}
          onOpenTabsChanged={onOpenTabsChanged}
        >
          {bodyMarkup}
        </Accordion>
      </div>
      <ConfirmationDialog ref={dialogRef}/>
      <ClipBoardDialog ref={clipboardDialogRef}/>
    </div>
  );
});

PageForm.propTypes = {
  pageId: PropTypes.string,
  className: PropTypes.string,
  model: PropTypes.object,
};

PageForm.defaultProps = {
  className: "",
  model: {
    title: "",
    subtitle: "",
    contents: [],
  },
};

export default PageForm;

const AccordionTabContent = ({
  counter,
  defaultTitle,
  menu, 
  className, 
  stateOpen, 
  ref, 
  model, 
  pageId, 
  Component,
  _ref,/*  index, createRefMaker */
}) => {
  const [headerSuffix, setHeaderSuffix] = React.useState(model.title ? `${model.title}` : defaultTitle);
  const onTitleChange = (title) => {
    setHeaderSuffix(title);
  };
  return (
    <AccordionTab
      header={counter  + headerSuffix}
      menu={menu}
      className={className}
      stateOpen={stateOpen}
    >
      <Component
        ref={_ref/* createRefMaker(index) */}
        className="w3-border-top-"
        model={model}
        pageId={pageId}
        onTitleChange={onTitleChange}
      />
    </AccordionTab>
  );
}